import { DocumentViewerModal } from "../../../features/beneficiary-document-reports/ui/DocumentViewerModal";
import { Modal } from "../../../shared/ui/modal";
import { SmartTable } from "../../../shared/ui/smart-table";
import { REVIEW_MODE_WITH_OBSERVATION } from "../model/alertManagementConstants";
import { getProductosGestionColumns } from "../model/getProductosAsociadosColumns";
import { AlertManagementDetailHeader } from "./AlertManagementDetailHeader";
import { AlertManagementIndeterminateRecalc } from "./AlertManagementIndeterminateRecalc";
import { AlertManagementDocsList } from "./AlertManagementDocsList";
import { AlertManagementReviewPanel } from "./AlertManagementReviewPanel";
import { AlertManagementTimeline } from "./AlertManagementTimeline";
import {
  DetailBackButton, DetailBodyCol, DetailBodyRow, DetailObservationBox, DetailSectionCard,
  DetailSectionTitle, DetailViewRoot, ReviewActionsRow,
  ReviewTextArea, WithObservationButton, WithoutObservationButton,
} from "./detail.styles";
import { SecondaryFilterButton } from "./filters.styles";

const PRODUCTOS_COLUMNS = getProductosGestionColumns();

// "Nuevo producto" (flujo INDETERMINADO): nombre + unidad + marca sugeridos.
// La unidad se resuelve por id contra las opciones de /api/lista/unidades/.
const buildNombreSugerido = (producto, unitOptions = []) => {
  if (!producto) return "";
  const unidadLabel =
    unitOptions.find((u) => u.value === producto.unidadSugeridaId)?.label ?? "--";
  return [producto.nombre_sugerido, unidadLabel, producto.marca_sugerida]
    .filter((part) => part != null && String(part).trim() !== "")
    .join(", ");
};

// Precio mínimo/máximo editables solo para jornadas de vigencia reciente:
// los dos años anteriores, sin contemplar el año actual.
const canEditPriceRange = (vigencia) => {
  const year = Number(vigencia);
  if (!Number.isFinite(year)) return false;
  const currentYear = new Date().getFullYear();
  return year >= currentYear - 2 && year < currentYear;
};

// Bloquea el envío cuando el precio mínimo supera al precio máximo.
const isPriceRangeInvalid = (values) => {
  const min = Number(values?.precioMinimo);
  const max = Number(values?.precioMaximo);
  return Number.isFinite(min) && Number.isFinite(max) && min > max;
};

export const AlertManagementGestionView = ({
  record, pillMap, documents, detailLoading, detailData, productosAsociados, timeline, timelineLoading,
  reviewMode, reviewObservation, reviewFiles, isConfirmOpen, isIndeterminate,
  confirmObservation, onConfirmObservationChange,
  recalc, jornadaVigencia, unitOptions,
  onBack, onViewDocument, onDownloadDocument,
  onSelectWithObservation, onSelectWithoutObservation,
  onObservationChange, onFilesChange, onSubmitWithObservation,
  onConfirmWithoutObservation, onCloseConfirm,
  pdfViewer, onClosePdfViewer, onDownloadFromViewer,
}) => (
  <DetailViewRoot>
    <DetailBackButton onClick={onBack}>Volver</DetailBackButton>
    <AlertManagementDetailHeader record={record} pillMap={pillMap} detailData={detailData} />
    <DetailBodyRow gutter={[16, 16]}>
      <DetailBodyCol xs={24} sm={24} md={24} lg={16} xl={16} xxl={17}>
        <AlertManagementDocsList
          documents={documents} loading={detailLoading}
          onView={onViewDocument} onDownload={onDownloadDocument}
        />
        <DetailSectionCard bordered={false}>
          <DetailSectionTitle>Productos asociados</DetailSectionTitle>
          <SmartTable
            rowKey="id_producto"
            columns={PRODUCTOS_COLUMNS}
            dataSource={productosAsociados ?? []}
            showPagination={false}
            showToolbar={false}
            enableRowSelection={false}
            showColumnSettings={false}
            emptyText="Sin productos asociados."
          />
          {isIndeterminate && (
            <AlertManagementIndeterminateRecalc
              productName={
                buildNombreSugerido(productosAsociados?.[0], unitOptions) ||
                productosAsociados?.[0]?.nombre
              }
              resultado={productosAsociados?.[0]?.resultado}
              canEditPriceRange={canEditPriceRange(jornadaVigencia)}
              values={recalc?.values}
              setField={recalc?.setField}
              observacion={recalc?.observacion}
              setObservacion={recalc?.setObservacion}
              result={recalc?.result}
              canCalculate={recalc?.canCalculate}
              calculate={recalc?.calculate}
            />
          )}
          {isIndeterminate && isPriceRangeInvalid(recalc?.values) && (
            <div style={{ color: "#dc2626", fontSize: "0.8rem", fontWeight: 600, marginTop: 8 }}>
              Corrige el rango de precios (el precio mínimo no puede ser mayor al máximo) para continuar.
            </div>
          )}
          <ReviewActionsRow>
            <WithObservationButton
              onClick={onSelectWithObservation}
              disabled={isIndeterminate && isPriceRangeInvalid(recalc?.values)}
            >
              Con observación
            </WithObservationButton>
            <WithoutObservationButton
              onClick={onSelectWithoutObservation}
              disabled={isIndeterminate && isPriceRangeInvalid(recalc?.values)}
            >
              Enviar
            </WithoutObservationButton>
          </ReviewActionsRow>
        </DetailSectionCard>
      </DetailBodyCol>
      <DetailBodyCol xs={24} sm={24} md={24} lg={8} xl={8} xxl={7}>
        <DetailSectionCard bordered={false}>
          <DetailSectionTitle>Observación justificativa</DetailSectionTitle>
          <DetailObservationBox>
            {detailData?.observacionJustificativa || record?.observacionJustificativa || "Sin observación justificativa registrada."}
          </DetailObservationBox>
        </DetailSectionCard>
        <AlertManagementTimeline items={timeline} loading={timelineLoading} />
        {reviewMode === REVIEW_MODE_WITH_OBSERVATION && (
          <AlertManagementReviewPanel
            observation={reviewObservation} files={reviewFiles}
            onChange={onObservationChange} onFilesChange={onFilesChange}
            onSubmit={onSubmitWithObservation}
          />
        )}
      </DetailBodyCol>
    </DetailBodyRow>
    <Modal isOpen={isConfirmOpen} onCloseModal={onCloseConfirm}
      title="Confirmar levantamiento" footer={null} width={760} centered>
      <div style={{ display: "grid", gap: 20 }}>
        <p style={{ margin: 0, color: "#0f172a", lineHeight: 1.6 }}>
          ¿Está seguro de levantar la alerta?
        </p>
        <ReviewTextArea
          value={confirmObservation}
          onChange={(e) => onConfirmObservationChange(e.target.value)}
          placeholder="La solicitud quedará en estado Resuelta, la categoría de alerta actual pasará a 0 - Sin alerta y la categoría anterior quedará registrada en el histórico."
        />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <SecondaryFilterButton onClick={onCloseConfirm}>Cancelar</SecondaryFilterButton>
          <WithoutObservationButton onClick={onConfirmWithoutObservation}>Confirmar</WithoutObservationButton>
        </div>
      </div>
    </Modal>
    <DocumentViewerModal isOpen={pdfViewer.isOpen} title="Visor de documento"
      subtitle={pdfViewer.title} documentUrl={pdfViewer.url}
      onClose={onClosePdfViewer} onDownload={onDownloadFromViewer} />
  </DetailViewRoot>
);